import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['tab', 'mobileTab', 'mobileDropdownButton']
  static values = { activeClass: String }

  connect() {
    // Remember the active state that matches the content currently on the page.
    // handleClick optimistically moves the highlight before a page visit completes;
    // without this, that transient highlight gets baked into Turbo's cached snapshot
    // and reappears out of sync with the content on a back/forward restore.
    this.commitActiveState()
    document.addEventListener('turbo:before-cache', this.restoreCommittedState)
  }

  disconnect() {
    document.removeEventListener('turbo:before-cache', this.restoreCommittedState)
  }

  get allTabs() {
    return [...this.tabTargets, ...this.mobileTabTargets]
  }

  tabTargetConnected(tab) {
    tab.addEventListener('click', this.handleClick)
  }

  tabTargetDisconnected(tab) {
    tab.removeEventListener('click', this.handleClick)
  }

  mobileTabTargetConnected(tab) {
    tab.addEventListener('click', this.handleClick)
  }

  mobileTabTargetDisconnected(tab) {
    tab.removeEventListener('click', this.handleClick)
  }

  handleClick = event => {
    // A cmd/ctrl/shift-click, a middle click, or a link that opens elsewhere loads the tab
    // in a different browser tab or window and leaves this page — and so its active tab —
    // untouched. Mirrors Turbo's own test for a click that drives a visit.
    if (!this.clickNavigatesThisPage(event)) return

    const clicked = event.currentTarget

    // desktop and mobile lists render the same tabs in the same order, so the
    // clicked link's counterpart in the other list sits at the same index
    const isMobile = this.mobileTabTargets.includes(clicked)
    const siblings = isMobile ? this.mobileTabTargets : this.tabTargets
    const counterpart = (isMobile ? this.tabTargets : this.mobileTabTargets)[siblings.indexOf(clicked)]

    this.markActive([clicked, counterpart].filter(Boolean))

    if (this.hasMobileDropdownButtonTarget) {
      this.mobileDropdownButtonTarget.innerHTML = clicked.innerHTML
    }

    // A tab that navigates a frame leaves this nav in place, so the controller never
    // reconnects to pick up fresh server-rendered state — the highlight we just moved
    // is the committed state, not a transient one.
    if (this.navigatesFrame(clicked)) this.commitActiveState()
  }

  clickNavigatesThisPage(event) {
    if (event.defaultPrevented || event.button > 0) return false
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false

    return event.currentTarget.getAttribute('target') !== '_blank'
  }

  navigatesFrame(tab) {
    const frame = tab.dataset.turboFrame
    return Boolean(frame) && frame !== '_top'
  }

  commitActiveState() {
    this.committedActiveTabs = this.allTabs.filter(tab => tab.classList.contains(this.activeClassValue))
    this.committedMobileButtonHTML = this.hasMobileDropdownButtonTarget
      ? this.mobileDropdownButtonTarget.innerHTML
      : null
  }

  markActive(activeTabs) {
    this.allTabs.forEach(tab => {
      const active = activeTabs.includes(tab)
      tab.classList.toggle(this.activeClassValue, active)
      if (active) {
        tab.setAttribute('aria-current', 'page')
      } else {
        tab.removeAttribute('aria-current')
      }
    })
  }

  // Restore the committed highlight before Turbo caches this page, so a cached
  // restore shows the tab that matches the content, not the one clicked to
  // navigate away.
  restoreCommittedState = () => {
    this.markActive(this.committedActiveTabs)
    if (this.hasMobileDropdownButtonTarget && this.committedMobileButtonHTML !== null) {
      this.mobileDropdownButtonTarget.innerHTML = this.committedMobileButtonHTML
    }
  }
}
